import {
  FormControlLabel,
  MenuItem,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputLabel,
  Grid,
  Divider,
  Typography,
} from '@mui/material';
import React from 'react';
import { WO2Checkbox, WO2Select, WO2ToggleSwitch } from 'src/common';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { BaseReportComponentProps, MdaReportTypeField, MdaReportTypes, SortByCashTransactions } from '../../store/types';
import { DateRange } from 'src/store';
import { ExpandMore } from '@mui/icons-material';
import { ReportGroupingOption } from '../ReportGroupingOption';

export interface CashTransactionsProps extends BaseReportComponentProps {
  setCashTransactionsSortByParameter: ActionCreatorWithPayload<SortByCashTransactions, string>;
  setDateRangeCashTransactionsParameter: ActionCreatorWithPayload<DateRange, string>;
  setCashTransactionsSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  expandCashTransactions: ActionCreatorWithoutPayload<string>;
  setCashTransactionsExcludeAudValue: ActionCreatorWithPayload<boolean, string>;
}

export const CashTransactions = ({
  parameters,
  createReportSortRequest,
  setCashTransactionsSelectedParameter,
  setCashTransactionsExcludeAudValue,
  setDateRangeCashTransactionsParameter,
  setCashTransactionsSortByParameter,
  expandCashTransactions,
  setGroupConsolidatedOption,
}: CashTransactionsProps): JSX.Element => {
  const handleClickCashTransactionsSelect = () => {
    setCashTransactionsSelectedParameter(!parameters.selectedReports?.cashTransactions.selected);
    createReportSortRequest(
      !parameters.selectedReports?.cashTransactions.selected,
      MdaReportTypes.CashTransactions.id,
      MdaReportTypes.CashTransactions.displayName
    );
  };

  const handleClickReportDateCashTransactions = (dates: DateRange) => {
    setDateRangeCashTransactionsParameter(dates);
  };

  return (
    <Accordion onChange={expandCashTransactions} expanded={parameters.selectedReports?.cashTransactions.expanded || false} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          sx={{ flex: 1 }}
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox
              color="primary"
              checked={parameters.selectedReports?.cashTransactions.selected}
              onChangeHandler={handleClickCashTransactionsSelect}
            />
          }
          label="Cash Transactions"
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker
            dateRange={{
              dateFrom: parameters?.selectedReports?.cashTransactions?.fromDate,
              dateTo: parameters?.selectedReports?.cashTransactions?.toDate,
              dateRangeType: parameters?.selectedReports?.cashTransactions?.dateRangeType,
            }}
            setDateRange={handleClickReportDateCashTransactions}
            inceptionDate={parameters.inceptionDate}
          />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <FormControl>
              <InputLabel id="sort-by-id">Sort by</InputLabel>
              <WO2Select
                labelId="sort-by-id"
                label="Sort by"
                onChange={(e) => setCashTransactionsSortByParameter(e.target.value as SortByCashTransactions)}
                value={parameters.selectedReports.cashTransactions.sortBy}
              >
                <MenuItem value={SortByCashTransactions.DateAscending}>Date Ascending</MenuItem>
                <MenuItem value={SortByCashTransactions.DateDescending}>Date Descending</MenuItem>
              </WO2Select>
            </FormControl>
          </Grid>
          <Grid item container xs={4} alignItems="center">
            <WO2ToggleSwitch
              onChange={(e) => setCashTransactionsExcludeAudValue(e.target.checked)}
              checked={parameters.selectedReports.cashTransactions.excludeAudValue}
              color="primary"
            />
            <Typography variant="h4">Exclude AUD Value</Typography>
          </Grid>
          {setGroupConsolidatedOption && (
            <Grid item container spacing={4} direction="column">
              <Grid item>
                <Divider />
              </Grid>
              <Grid item>
                <ReportGroupingOption
                  value={parameters.selectedReports.cashTransactions.groupOption}
                  setGroupConsolidatedOption={setGroupConsolidatedOption}
                  report={MdaReportTypeField.CashTransactions}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
